const getWidgetInstanceKey = (field) =>
  String(field?.field_instance_id ?? field?.name ?? "");

export const updatePreviewFieldInstanceLabel = (
  fields = [],
  targetField,
  nextLabel,
) => {
  const targetKey = getWidgetInstanceKey(targetField);
  const normalizedLabel = String(nextLabel ?? "").trim();

  if (!targetKey || !normalizedLabel) return fields;

  let changed = false;
  const updatedFields = fields.map((field) => {
    if (getWidgetInstanceKey(field) !== targetKey) return field;

    if (
      field?.field_label === normalizedLabel &&
      field?.label === normalizedLabel
    ) {
      return field;
    }

    changed = true;
    return {
      ...field,
      field_label: normalizedLabel,
      label: normalizedLabel,
    };
  });

  return changed ? updatedFields : fields;
};

export const isPreviewGroupLabelRecord = (
  field,
  groupType = field?.field_type,
) => {
  const isZero = (value) =>
    value !== null &&
    value !== undefined &&
    value !== "" &&
    Number.isFinite(Number(value)) &&
    Number(value) === 0;

  return (
    Boolean(field?.field_id) &&
    ["checkboxGroup", "selectGroup"].includes(groupType) &&
    field.hidden === true &&
    field.type === "PDFTextField" &&
    [
      [field.x, field.rect?.X],
      [field.y, field.rect?.Y],
      [field.width, field.rect?.W],
      [field.height, field.rect?.H],
    ].every(([value, rectValue]) => isZero(value ?? rectValue))
  );
};

export const updatePreviewGroupInstanceLabel = (
  fields = [],
  targetGroup,
  nextLabel,
) => {
  const groupId = targetGroup?.field_id;
  const groupType = targetGroup?.field_type ?? targetGroup?.type;
  const normalizedLabel = String(nextLabel ?? "").trim();

  if (
    !groupId ||
    !["checkboxGroup", "selectGroup"].includes(groupType) ||
    !normalizedLabel
  ) {
    return fields;
  }

  const groupInstances = fields.filter((field) => field?.field_id === groupId);
  const existingGroup =
    groupInstances.find((field) =>
      isPreviewGroupLabelRecord(field, groupType),
    ) ||
    (targetGroup?.field_instance_id
      ? groupInstances.find(
          (field) => field.field_instance_id === targetGroup.field_instance_id,
        )
      : groupInstances.length === 1
        ? groupInstances[0]
        : groupInstances.find(
            (field) => targetGroup?.name && field.name === targetGroup.name,
          ));

  if (
    existingGroup &&
    (isPreviewGroupLabelRecord(existingGroup, groupType) ||
      groupType !== "selectGroup")
  ) {
    if (
      existingGroup.field_label === normalizedLabel &&
      existingGroup.label === normalizedLabel
    ) {
      return fields;
    }

    return fields.map((field) =>
      field === existingGroup
        ? { ...field, field_label: normalizedLabel, label: normalizedLabel }
        : field,
    );
  }

  // Do not create duplicates or update a different instance for a stale target.
  if (
    (groupType !== "selectGroup" && groupInstances.length) ||
    (targetGroup?.field_instance_id && !existingGroup)
  ) {
    return fields;
  }

  const optionIds = new Set(
    (targetGroup.options || [])
      .map((option) => option?.field_id ?? option?.value)
      .filter(Boolean),
  );
  const linkedOptions = fields.filter(
    (field) =>
      !isPreviewGroupLabelRecord(field, groupType) &&
      (optionIds.has(field?.field_id) ||
        field?.parent_field_id === groupId ||
        (groupType === "selectGroup" && field === existingGroup)),
  );
  if (!linkedOptions.length) return fields;

  const firstOption = linkedOptions[0];
  const baseName = String(groupId);
  const usedNames = new Set(fields.map((field) => field?.name));
  let name = baseName;
  let suffix = 1;
  while (usedNames.has(name)) {
    name = `${baseName}__group_${suffix}`;
    suffix += 1;
  }

  const optionOrders = linkedOptions
    .map((field) => field?.order)
    .filter(
      (order) =>
        order !== null &&
        order !== undefined &&
        order !== "" &&
        Number.isFinite(Number(order)),
    )
    .map(Number);
  const page = firstOption.page ?? firstOption.page_num ?? 1;

  // A generated preview group has no canvas widget. Persist only its instance
  // label and placement metadata, not the preview's options or selected value.
  const groupInstance = {
    name,
    field_id: groupId,
    field_label: normalizedLabel,
    label: normalizedLabel,
    field_type: groupType,
    type: "PDFTextField",
    hidden: true,
    form_id: firstOption.form_id,
    block_id:
      targetGroup.block_id ?? firstOption.block_id ?? firstOption.section,
    block_label:
      targetGroup.block_label ?? firstOption.block_label ?? firstOption.section,
    ...(optionOrders.length
      ? { order: Math.min(...optionOrders) }
      : targetGroup.order != null
        ? { order: targetGroup.order }
        : {}),
    page,
    page_num: page,
    x: 0,
    y: 0,
    width: 0,
    height: 0,
    rect: { X: 0, Y: 0, W: 0, H: 0 },
    fontSize: 10,
    fontSizes: [],
    autoFontSize: true,
  };

  return [...fields, groupInstance];
};

export const cleanupOrphanPreviewGroupLabels = (
  fields = [],
  catalog = [],
  previousFields = [],
) => {
  const groupsById = new Map(
    catalog
      .filter((field) =>
        ["checkboxGroup", "selectGroup"].includes(field?.field_type),
      )
      .map((field) => [field.field_id, field]),
  );

  const isLabelRecord = (field) =>
    isPreviewGroupLabelRecord(
      field,
      groupsById.get(field?.field_id)?.field_type ?? field?.field_type,
    );

  const previousWidgets = previousFields.filter(
    (field) => !isLabelRecord(field),
  );

  const remainingWidgets = fields.filter((field) => !isLabelRecord(field));

  const cleanedFields = fields.filter((group) => {
    if (!isLabelRecord(group)) return true;

    const definition = groupsById.get(group.field_id);

    // Do not guess before the catalog is available.
    if (!definition) return true;

    const optionIds = new Set(
      [
        ...(definition.options || []).map(
          (option) => option?.field_id ?? option?.value,
        ),
        ...catalog
          .filter((field) => field?.parent_field_id === group.field_id)
          .map((field) => field.field_id),
      ].filter(Boolean),
    );

    const belongsToGroup = (field) =>
      Boolean(field?.field_id) &&
      (field.field_id === group.field_id ||
        optionIds.has(field.field_id) ||
        field.parent_field_id === group.field_id);

    const hadWidget = previousWidgets.some(belongsToGroup);
    const hasWidget = remainingWidgets.some(belongsToGroup);

    // Remove only when this update removes the final linked widget.
    return !hadWidget || hasWidget;
  });

  return cleanedFields.length === fields.length ? fields : cleanedFields;
};
